import { sha256 } from '@noble/hashes/sha2';
import { generateIdempotencyKey } from './auth.js';
import { stableStringify } from './operation.js';
/**
 * 会跨进程边界调用模型、对象存储等外部系统的过程。
 * 这些过程不能在用户超时后换一把键重试，否则第一次可能已经在外部生效。
 */
export const PERSISTENT_OPERATION_PROCEDURES = new Set([
    'inquiry.draft',
    'quotation.prepareArtifact',
    'quotation.newVersion',
    'quotation.downloadArtifact',
    'compose.generate',
    'intelligence.analyze',
    'businessReview.operatingAdvice',
    'businessReview.approvalAdvice',
    'recommend.suggest',
    'knowledge.ask',
    'assistant.transcribe',
    'assistant.send',
    'assistant.refreshAnswer',
    'assistant.prepareFollowup',
    'assistant.prepareOutcome',
    'assistant.confirmPreparedProposal',
    // Keep a private destructive-action retry bound to its original actor and thread.
    'assistant.deleteThread',
    'document.finalizeUpload',
    'document.createDownloadGrant',
    'llm.testEndpoint',
    'auth.switchTenant',
]);
const PREFIX = 'keji.transport-op:';
function digest(value) {
    const bytes = sha256(new TextEncoder().encode(stableStringify(value)));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}
function headerValue(headers, name) {
    if (!headers)
        return null;
    const lower = name.toLowerCase();
    if (typeof headers === 'object' &&
        headers !== null &&
        'get' in headers &&
        typeof headers.get === 'function') {
        const value = Reflect.apply(headers.get, headers, [name]);
        return typeof value === 'string' ? value : null;
    }
    if (typeof headers !== 'object')
        return null;
    const entries = Array.isArray(headers)
        ? headers
        : Object.entries(headers);
    const found = entries.find(([key]) => key.toLowerCase() === lower);
    return found ? String(found[1]) : null;
}
/** HTTP 已给出确定业务结论时才释放；超时、冲突处理中、限流和服务端故障都保留原键。 */
export function shouldReleasePersistentOperation(status) {
    if (status >= 200 && status < 400)
        return true;
    return [400, 401, 403, 404, 405, 406, 410, 411, 412, 413, 414, 415, 416, 422].includes(status);
}
/**
 * 只持久化入参摘要与操作键，不落业务正文、口令或模型密钥。
 * namespace 必须含租户和用户，避免退出后另一账号继承前一操作者的键。
 */
export function createPersistentOperationRegistry(storage, options = {}) {
    const scopesByKey = new Map();
    const keyFor = (namespace, procedure, input) => `${PREFIX}${namespace}:${procedure}:${digest(input)}`;
    return {
        pin(namespace, procedure, input) {
            const scope = keyFor(namespace, procedure, input);
            try {
                const raw = storage.getItem(scope);
                if (raw) {
                    const parsed = JSON.parse(raw);
                    if (typeof parsed.key === 'string' && parsed.key.length > 0) {
                        scopesByKey.set(parsed.key, scope);
                        return parsed.key;
                    }
                }
            }
            catch {
                // 存储损坏时退化为本次调用键，不能阻断业务。
            }
            const key = (options.generateKey ?? generateIdempotencyKey)();
            scopesByKey.set(key, scope);
            try {
                storage.setItem(scope, JSON.stringify({ key, pinnedAt: new Date().toISOString() }));
            }
            catch {
                /* 见上 */
            }
            return key;
        },
        settleFromResponse(headers, status) {
            if (!shouldReleasePersistentOperation(status))
                return;
            const key = headerValue(headers, 'Idempotency-Key');
            if (!key)
                return;
            const scope = scopesByKey.get(key);
            if (!scope)
                return;
            scopesByKey.delete(key);
            try {
                storage.removeItem(scope);
            }
            catch {
                /* 存储不可用不改变请求结果 */
            }
        },
        discard(namespace, procedure, input) {
            const scope = keyFor(namespace, procedure, input);
            try {
                storage.removeItem(scope);
            }
            catch {
                /* 同上 */
            }
        },
    };
}
//# sourceMappingURL=operation-registry.js.map